import { WIDGET_LIMITS } from "./widget-model";

/** Local-test hosts the API accepts besides real domains (the only IP literals it allows). */
const LOCAL_HOSTS: ReadonlySet<string> = new Set(["localhost", "127.0.0.1", "[::1]"]);
const DOMAIN_PATTERN = /^(\*\.)?(?=.{1,253}$)([a-z\d](?:[a-z\d-]{0,61}[a-z\d])?\.)+[a-z]{2,63}$/;
const HOST_AND_PORT = /^(\[[^\]]*\]|[^:]*)(?::(\d+))?$/;
const URL_SCHEME = /^[a-z][a-z\d+.-]*:\/\//;
const MAX_PORT = 65_535;
const IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}(\/(3[0-2]|[12]?\d))?$/;
const IPV6 = /^[\da-f:]+(\/(12[0-8]|1[01]\d|\d?\d))?$/i;
const MAX_NOTE = 80;

/**
 * Typed or pasted text → an allowed-domain entry the API accepts: `host`, `host:port` or `*.host`.
 * `https://Help.Example.com/path` → `help.example.com`; `http://localhost:3000/` → `localhost:3000`.
 * Null when it isn't one (any other IP address, a port outside 1-65535, a bare wildcard...).
 */
export function normalizeDomain(input: string): string | null {
  const authority = input.trim().toLowerCase().replace(URL_SCHEME, "").replace(/[/?#].*$/, "");
  const match = HOST_AND_PORT.exec(authority);
  if (!match) return null;
  const host = (match[1] ?? "").replace(/\.$/, "");
  const port = match[2];
  if (port !== undefined && (Number(port) < 1 || Number(port) > MAX_PORT)) return null;
  if (!LOCAL_HOSTS.has(host) && !DOMAIN_PATTERN.test(host)) return null;
  return port === undefined ? host : `${host}:${String(Number(port))}`;
}

export interface BlockedIp {
  cidr: string;
  note: string;
}

export function isIpOrCidr(value: string): boolean {
  return IPV4.test(value) || (value.includes(":") && IPV6.test(value));
}

export type ParseBlockedResult = { ok: true; entries: BlockedIp[] } | { ok: false; line: number };

/** Textarea → entries: one IP/CIDR per line, text after `#` is a note. */
export function parseBlockedIps(text: string): ParseBlockedResult {
  const entries: BlockedIp[] = [];
  const lines = text.split(/\r?\n/);
  for (const [index, raw] of lines.entries()) {
    const [address = "", ...rest] = raw.split("#");
    const cidr = address.trim();
    if (!cidr) continue;
    if (!isIpOrCidr(cidr)) return { ok: false, line: index + 1 };
    entries.push({ cidr, note: rest.join("#").trim().slice(0, MAX_NOTE) });
  }
  return { ok: true, entries: entries.slice(0, WIDGET_LIMITS.maxBlockedIps) };
}

export function formatBlockedIps(entries: readonly { cidr?: unknown; note?: unknown }[]): string {
  return entries
    .filter((e): e is { cidr: string; note?: unknown } => typeof e.cidr === "string")
    .map((e) => (typeof e.note === "string" && e.note ? `${e.cidr}  # ${e.note}` : e.cidr))
    .join("\n");
}

export function rateLimit(limits: Record<string, unknown>, key: string, fallback: number): number {
  const value = limits[key];
  return typeof value === "number" ? value : fallback;
}
